import { quote, validQuantity, reservationAmounts } from '@theme/intecular-commerce';
import { StandardEvents } from '@shopify/events';

function analytics(name, detail = {}) {
  if (!window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()) return;
  window.Shopify?.analytics?.publish?.(`intecular_${name}`, { ...detail, currency: window.Shopify?.currency?.active, page_role: document.querySelector('[data-ic-role]')?.dataset.icRole || 'utility' });
}

class ICQuantity extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.planObserver?.disconnect();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.input = this.querySelector('input[name="quantity"]');
    this.input.id = `ic-quantity-${this.closest('.shopify-section').id.replace('shopify-section-', '')}`;
    this.strings = JSON.parse(this.querySelector('[data-ic-strings]').textContent);
    let data;
    try { data = JSON.parse(this.querySelector('[data-ic-config]').textContent); } catch { data = null; }
    this.config = this.dataset.configEnabled === 'true' ? data?.products?.[this.dataset.handle] : null;
    this.productForm = this.closest('product-form-component');
    try { this.preorderPrices = JSON.parse(this.productForm?.dataset.preorderPrices || 'null'); } catch { this.preorderPrices = null; }
    this.planId = this.productForm?.querySelector('input[name="selling_plan"]')?.value || '';
    this.serverNow = Number(this.dataset.now) * 1000;
    this.started = performance.now();
    this.existing = 0;
    this.cartKnown = false;
    this.classList.add('ic-enhanced');
    const campaignQuantity = new URLSearchParams(location.search).get('quantity');
    if (campaignQuantity && this.valid(campaignQuantity)) this.setQuantity(campaignQuantity);
    this.addEventListener('change', e => {
      if (e.target.name === 'ic-quantity-choice') this.setQuantity(e.target.value);
      this.render();
      analytics('quantity_selection', { product_id: this.dataset.productId, variant_id: this.dataset.variantId, quantity: Number(this.input.value), placement: 'purchase' });
    }, { signal });
    this.addEventListener('input', e => { if (e.target === this.input) this.render(); }, { signal });
    this.addEventListener('quantity-selector:update', () => this.render(), { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, event => {
      this.cartKnown = false;
      this.render();
      event.promise?.then(() => this.refreshCart(), () => this.refreshCart());
    }, { signal });
    window.addEventListener('pageshow', () => this.refreshCart(), { signal });
    window.addEventListener('focus', () => this.refreshCart(), { signal });
    if (this.preorderPrices) {
      this.planObserver = new MutationObserver(() => {
        const planId = this.productForm.querySelector('input[name="selling_plan"]')?.value || '';
        if (planId === this.planId) return;
        this.planId = planId;
        this.cartKnown = false;
        this.render();
        this.refreshCart();
      });
      this.planObserver.observe(this.productForm, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });
    }
    this.render();
    this.refreshCart();
  }
  disconnectedCallback() { this.abort?.abort(); this.planObserver?.disconnect(); }
  valid(value) { return validQuantity(value, { min: Number(this.input.min) || 1, max: Math.min(Number(this.input.max) || Number.MAX_SAFE_INTEGER, Math.floor(Number.MAX_SAFE_INTEGER / Math.max(1, Number(this.dataset.unit))) - (this.existing || 0)), step: Number(this.input.step) || 1 }); }
  setQuantity(value) {
    if (!this.valid(value)) return;
    this.input.value = value;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
  async refreshCart() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, { cache: 'no-store' });
      if (!response.ok) throw Error('Cart unavailable');
      const cart = await response.json();
      if (!this.isConnected) return;
      const productItems = cart.items.filter(i => String(i.product_id) === this.dataset.productId);
      const eligibleItems = productItems.filter(i => this.preorderPrices ? String(i.selling_plan_allocation?.selling_plan?.id) === this.planId : !i.selling_plan_allocation);
      this.existing = eligibleItems.reduce((n, i) => n + i.quantity, 0);
      this.cartKnown = cart.currency === this.dataset.currency && (!this.preorderPrices || eligibleItems.length === productItems.length);
    } catch { this.cartKnown = false; }
    this.render();
  }
  render() {
    if (!this.input) return;
    const valid = this.valid(this.input.value);
    this.input.setCustomValidity(valid ? '' : this.strings.invalid);
    const error = this.querySelector('[data-ic-error]');
    error.hidden = valid; error.textContent = valid ? '' : this.strings.invalid;
    for (const radio of this.querySelectorAll('[name="ic-quantity-choice"]')) radio.checked = radio.value === this.input.value;
    if (!valid) return;
    const result = quote({ unit: Number(this.dataset.unit), planUnit: this.preorderPrices?.[this.planId], quantity: Number(this.input.value), existing: this.existing, productId: this.dataset.productId, variantId: this.dataset.variantId, currency: this.dataset.currency, country: this.dataset.country, config: this.cartKnown ? this.config : null, now: this.serverNow + performance.now() - this.started, compare: Number(this.dataset.compare) });
    const money = n => new Intl.NumberFormat(document.documentElement.lang, { style: 'currency', currency: this.dataset.currency }).format(n / 100);
    const compare = this.querySelector('[data-ic-compare]');
    if (compare) {
      compare.hidden = !result.compare;
      compare.querySelector('del').textContent = result.compare ? `${money(result.compare)} ${this.dataset.currency}` : '';
    }
    for (const radio of this.querySelectorAll('[name="ic-quantity-choice"]')) {
      const option = quote({ unit: Number(this.dataset.unit), planUnit: this.preorderPrices?.[this.planId], quantity: Number(radio.value), existing: this.existing, productId: this.dataset.productId, variantId: this.dataset.variantId, currency: this.dataset.currency, country: this.dataset.country, config: this.cartKnown ? this.config : null, now: this.serverNow + performance.now() - this.started, compare: Number(this.dataset.compare) });
      const label = radio.closest('label');
      const price = label.querySelector('[data-ic-option-price]');
      const saving = label.querySelector('[data-ic-option-saving]');
      if (price) price.textContent = `${money(option.perDevice)} each`;
      const saved = (option.compare || Number(this.dataset.unit) * option.quantity) - option.total;
      if (saving) saving.textContent = saved > 0 ? `Save ${money(saved)}` : '';
    }
    this.querySelector('[data-ic-total-label]').textContent = this.strings.totalLabel;
    this.querySelector('[data-ic-total]').textContent = `${money(result.total)} ${this.dataset.currency}`;
    this.querySelector('[data-ic-per]').textContent = this.strings.per.replace('[amount]', money(result.perDevice));
    const msrpSaved = result.compare ? result.compare - result.total : 0;
    this.querySelector('[data-ic-savings]').textContent = ['invisoutlet-pro', 'invisoutlet-aura'].includes(this.dataset.handle)
      ? (msrpSaved > 0 ? `Save ${money(msrpSaved)} (${Math.round(msrpSaved / result.compare * 100)}% off MSRP)` : '')
      : (result.verified && result.savings ? this.strings.savings.replace('[amount]', money(result.savings)) : '');
    this.querySelector('[data-ic-existing]').textContent = this.cartKnown && this.existing ? this.strings.existing.replace('[existing]', this.existing).replace('[combined]', result.eligible).replace('[amount]', money(result.combined)) : '';
    this.querySelector('[data-ic-price-note]').textContent = result.verified ? this.strings.estimate : this.strings.fallback;
    const sticky = document.querySelector('[data-ic-sticky-total]');
    if (sticky) sticky.textContent = money(result.total);
    const stickyCompare = document.querySelector('[data-ic-sticky-compare]');
    if (stickyCompare) {
      stickyCompare.hidden = !result.compare;
      stickyCompare.querySelector('del').textContent = result.compare ? `${money(result.compare)} ${this.dataset.currency}` : '';
    }
  }
}
if (!customElements.get('ic-quantity')) customElements.define('ic-quantity', ICQuantity);

let cartBusy = 0;
let uncertain = false;
document.addEventListener('submit', event => {
  if (!event.target.matches('product-form-component form')) return;
  if (cartBusy || uncertain || !event.target.checkValidity()) { event.preventDefault(); event.stopImmediatePropagation(); }
}, true);
document.addEventListener(StandardEvents.cartLinesUpdate, event => {
  if (!event.promise) return;
  cartBusy += 1;
  document.documentElement.setAttribute('data-ic-cart-busy', '');
  event.promise.then(() => { if (!uncertain && cartNotice) cartNotice.hidden = true; }, () => {});
  event.promise.finally(() => { cartBusy -= 1; if (!cartBusy) document.documentElement.removeAttribute('data-ic-cart-busy'); }).catch(() => {});
});
const cartNotice = document.getElementById('ic-cart-reconcile');
const uncertainMessage = cartNotice?.firstChild?.textContent;
document.addEventListener('intecular:cart-uncertain', () => {
  uncertain = true;
  if (cartNotice) { cartNotice.firstChild.textContent = uncertainMessage; cartNotice.hidden = false; cartNotice.focus(); }
});
document.addEventListener('intecular:cart-refresh-failed', () => {
  if (uncertain || !cartNotice) return;
  cartNotice.firstChild.textContent = `${cartNotice.dataset.refreshMessage || 'Your cart was updated, but the cart display could not refresh. Review your cart before continuing.'} `;
  cartNotice.hidden = false;
  cartNotice.focus();
});
document.addEventListener('keydown', event => {
  if (event.key !== 'Escape') return;
  const open = document.querySelector('.ic-local details[open]');
  if (open) { open.open = false; open.querySelector('summary').focus(); }
});
document.addEventListener('click', event => {
  for (const open of document.querySelectorAll('.ic-local details[open]')) if (!open.contains(event.target) || event.target.closest('a')) open.open = false;
  const action = event.target.closest('[data-ic-event]');
  if (action) analytics(action.dataset.icEvent, { product_id: action.closest('[data-ic-product]')?.dataset.icProduct, placement: action.dataset.placement || 'navigation' });
});
function updateReservation(event) {
  const reservation = event.target.closest('[data-ic-reservation]');
  if (!reservation || event.target.name !== 'quantity') return;
  const valid = validQuantity(event.target.value, { max: 25 });
  reservation.querySelector('[data-reservation-error]').hidden = valid;
  if (!valid) return;
  const amounts = reservationAmounts(Number(reservation.dataset.unit), Number(event.target.value));
  for (const [key, value] of Object.entries(amounts)) for (const target of reservation.querySelectorAll(`[data-reservation-${key}]`)) target.textContent = new Intl.NumberFormat(document.documentElement.lang, { style: 'currency', currency: reservation.dataset.currency }).format(value / 100);
}
document.addEventListener('input', updateReservation, true);
document.addEventListener('change', updateReservation, true);
document.addEventListener('quantity-selector:update', event => {
  const input = event.target.closest('[data-reservation-form]')?.querySelector('input[name="quantity"]');
  if (input) updateReservation({ target: input });
});
window.addEventListener('pageshow', () => { for (const input of document.querySelectorAll('[data-reservation-form] input[name=quantity]')) updateReservation({target:input}); });
if (document.querySelector('[data-ic-role="landing"]')) analytics('landing_view', { product_id: document.querySelector('[data-ic-product]')?.dataset.icProduct });

// Recheck the server-derived campaign clock when a tab remains open across cutoff.
document.addEventListener('submit', event => {
  if (!event.target.matches('[data-reservation-form]')) return;
  const reservation = event.target.closest('[data-ic-reservation]');
  const now = Number(reservation.dataset.now) * 1000 + performance.now();
  if (now >= Number(reservation.dataset.cutoff) * 1000 || !event.target.checkValidity()) {
    event.preventDefault();
    for (const button of event.target.querySelectorAll('button[type=submit]')) button.disabled = true;
    if (now >= Number(reservation.dataset.cutoff) * 1000) location.reload();
  }
}, true);
for (const input of document.querySelectorAll('[data-reservation-form] input[name=quantity]')) { input.readOnly=false; updateReservation({target:input}); }
document.documentElement.dataset.icReady = 'true';
